import { UserService, ProfileService } from '@nuskin/ns-account';
import { axios } from '@nuskin/axios-util';
import { RunConfigService, ConfigService } from '@nuskin/ns-util';
import { events } from '@nuskin/ns-util';
import regexJson from '../regex.json';

const emailValidator = regexJson.emailValidator;

const TEST_BASE_URL = 'https://testapi.cloud.nuskin.com/email-tools/v1';
const PROD_BASE_URL = 'https://api.cloud.nuskin.com/email-tools/v1';
const http = axios.create();

export default {
    getCountry: getCountry,
    getLanguage: getLanguage,
    isHomeMarket: isHomeMarket,
    publishAnalytics: publishAnalytics,
    getPhoneCountries: getPhoneCountries,
    isDuplicateEmail: isDuplicateEmail,
    isEmailVerified: isEmailVerified,
    validateEmail: validateEmail,
    sendVerificationEmail: sendVerificationEmail,
    getBaseUrl: getBaseUrl,
    getClientID: getClientID,
    updateEmail: updateEmail,
    getOktaToken: getOktaToken,
    verifyStatus: verifyStatus
};

/**
 * Get the market country from the url
 *
 * @return {String} The country code, e.g. ['US', 'CA', 'CN']
 */
function getCountry() {
    let marketRegex = new RegExp(/[a-z]{2}_[A-Z]{2}/);
    let marketLang = marketRegex.exec(window.location.pathname)[0];
    return marketLang.split('_')[1];
    //return UserService.getUser().countryCode;
}

/**
 * Get okta token
 *
 * @return {String} 
 */
function getOktaToken() {
    return JSON.parse(sessionStorage.getItem('oktaTokens'))?.accessToken || '';
}

/**
 * Get based AWS url
 *
 * @return {String} The url, e.g. {'string'}
 */
function getBaseUrl() {
    const runConfigEnv = RunConfigService.getEnvironmentCode();
    return runConfigEnv === 'test'
        ? TEST_BASE_URL
        : PROD_BASE_URL
    ;
}

/**
 * Get Client ID
 *
 * @return {String} The code id, e.g. {'string'}
 */
function getClientID() {
    const marketConfig = ConfigService.getMarketConfig();
    return marketConfig.checkout.clientId
}

/**
 * get Boolean array list form Email validation
 *
 * @return {[isFormatValid, isDomainValid, isAvailable]} The country code, e.g. ['true', 'true', 'true']
 */
async function isDuplicateEmail(email) {
    let isFormatValid = false;
    let isAvailable = false;
    let isDomainValid = false;

    const sapId = UserService.getUser().id;

    const baseurl = getBaseUrl()
    let url = `${baseurl}/emailInfo?email=${email}&sapId=${sapId}`;
    try {
        let response = await http.get(url, { headers: { client_id: getClientID() } });
        isFormatValid = response.data.data.isFormatValid;
        isAvailable = response.data.data.isAvailable;
        isDomainValid = response.data.data.isDomainValid;
    } catch (e) {
        console.error(e, 'Error checking signup');
    }

    return [isFormatValid, isDomainValid, isAvailable];
}

/**
 * get Boolean for checking Email Verified
 *
 * @return  {Boolean} , e.g. {true}
 */
async function isEmailVerified(email) {
    let isEmailVerified = false;

    const baseurl = getBaseUrl()
    let url = `${baseurl}/emailInfo?email=${email}`;
    try {
        let response = await http.get(url, { headers: { client_id: getClientID() } });
        isEmailVerified = response.data.data.isVerified;
    } catch (e) {
        console.error(e, 'Error checking email verification');
    }

    return isEmailVerified;
}

/**
 * Set a flag to trigger an email validation flow
 * @param {String} email
 * @param {String} eid
 * @return {Promise<EventResult>} messages to be sent to client, if any
 */

async function sendVerificationEmail(email) {
    let info = {
        status: null,
        token: ''
    };
    let locale = getLanguage() + '_' + getCountry();
    const baseurl = getBaseUrl()
    const sapId = UserService.getUser().id
    try {
        const data = JSON.stringify({
            'email': email, 
            'locale': locale,
            'sapId': sapId
        });
        const emailUrl = `${baseurl}/sendVerification`;
        const emailResponse = await http.post(
            emailUrl,
            data,
            {
                headers: {
                    'client_id': getClientID(),
                    'Authorization': `Bearer ${getOktaToken()}`
                }
            });
        info.status = emailResponse.data.status;
        info.token = emailResponse.data.data.token;
    } catch (err) {
        if (err.response) {
            info.status = err.response.status;
        }
    }
    return info
}

/**
 * Set a flag to trigger an email validation flow
 * @param {String} email
 * @param {String} sapid
 * @param {String} eid
 * @return {Promise<EventResult>} messages to be sent to client, if any
 */

async function updateEmail(email) {
    let result = {};
    const baseurl = getBaseUrl()
    const eid = UserService.getUser().eid;
    const sapId = UserService.getUser().id;
    try {
        const data = JSON.stringify({
            'email': email,
            'sapId': sapId,
            'eid': eid
        });
        const emailUrl = `${baseurl}/editEmail`;
        
        result = await http.post(
            emailUrl,
            data,
            {
                headers: {
                    'client_id': getClientID(),
                    'Authorization': `Bearer ${getOktaToken()}`
                }
            });
    } catch (err) {
        if (err.response) {
            result.status = err.response.status;
        }
    }
    return result
}

/**
 * check verify email status
 *
 * @return  {email, status} , e.g. {true}
 */
async function verifyStatus() {
    const baseurl = getBaseUrl()
    const sapId = UserService.getUser().id;
    let response;

    let url = `${baseurl}/verifyStatus?sapId=${sapId}`;
    try {
        response =  await http.get(url, {
            headers: { 
                'Authorization': `Bearer ${getOktaToken()}`
            }
        });
    } catch (e) {
        console.error(e, 'Error checking user verify status');
    }
    return response?.data?.data;
}

/**
 * check verification email sent or not
 *
 * @return  {success: boolean} , e.g. {true}
 */
async function validateEmail(uuid) {
    let data = {
        success: false
    };
    const baseurl = getBaseUrl()
    let url = `${baseurl}/validateEmail?uuid=${uuid}`;
    try {
        let response = await http.get(url, {
            headers: { client_id: getClientID() }
        });
        data = response.data.data.dynamoResponse.value;
    } catch (e) {
        console.error(e, 'Error checking email verification');
    }
    return data;
}

/**
 * Get the market language from the url
 *
 * @return {String} The language code, e.g. ['en', 'fr', 'zh']
 */
function getLanguage() {
    let marketRegex = new RegExp(/[a-z]{2}_[A-Z]{2}/);
    let marketLang = marketRegex.exec(window.location.pathname)[0];
    return marketLang.split('_')[0];
}
/**
 * Get the translated countries for mobile phone selection
 */

function getPhoneCountries() {
    let languageList = [];
    ProfileService.getCountryAndLanguage(getLanguage()).then(function(data) {
        let langKeys = Object.keys(data.data);

        langKeys.forEach((key) => {
            // eslint-disable-next-line security/detect-object-injection
            languageList[key] = data.data[key].name;
        });
    });
    return languageList;
}

/**
 * Is the user in their home market
 *
 * @return {Boolean}
 */
function isHomeMarket() {
    return UserService.getUser().countryCd === getCountry();
}

/**
 * A wrapper to shortcut publish event
 * @param {String} eventCategory
 * @param {String} eventType
 * @param {String} eventTrigger
 */
function publishAnalytics(eventCategory, eventType, eventTrigger, account = null) {
    events.publish(events.profile.ANALYTICS, {
        category: eventCategory,
        eventType: eventType,
        eventTrigger: eventTrigger
    });
    if (account) {
        emailNotification(account);
    }
}

function emailNotification(account) {
    const template = 'profileUpdateNotification';
    let locale = getLanguage() + '_' + getCountry();

    const username = UserService.getUser().username;
    const email = username && username.match(emailValidator) ? username : account.email;
    ProfileService.emailNotification(template, email, locale);
}